import { Injectable, inject } from '@angular/core';
import { SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly settingsService = inject(SettingsService);
  private systemThemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  constructor() {
    // Listen for system theme changes
    this.systemThemeMediaQuery.addEventListener('change', () => {
      const settings = this.settingsService.getSettings();
      if (settings.theme === 'system') {
        this.applySystemTheme();
      }
    });
  }

  /**
   * Initialize theme on application startup
   */
  initializeTheme(): void {
    const settings = this.settingsService.getSettings();
    this.applyTheme(settings.theme);
  }

  /**
   * Apply the specified theme
   */
  applyTheme(theme: 'light' | 'dark' | 'system'): void {
    const body = document.body;
    
    switch (theme) {
      case 'light':
        body.classList.remove('dark');
        break;
      case 'dark':
        body.classList.add('dark');
        break;
      case 'system':
        this.applySystemTheme();
        break;
    }
  }

  /**
   * Apply theme based on system preference
   */
  private applySystemTheme(): void {
    const body = document.body;
    const prefersDark = this.systemThemeMediaQuery.matches;
    
    if (prefersDark) {
      body.classList.add('dark');
    } else {
      body.classList.remove('dark');
    }
  }

  /**
   * Get current effective theme (resolves 'system' to actual theme)
   */
  getCurrentEffectiveTheme(): 'light' | 'dark' {
    const settings = this.settingsService.getSettings();
    
    if (settings.theme === 'system') {
      return this.systemThemeMediaQuery.matches ? 'dark' : 'light';
    }
    
    return settings.theme;
  }

  /**
   * Check if dark mode is currently active
   */
  isDarkMode(): boolean {
    return this.getCurrentEffectiveTheme() === 'dark';
  }
}